import { financialScope } from '../utils/financialScope';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { STAGE_COLORS } from '../constants/brand';

const StatCard = ({ icon, label, value, value2, sub, color, onClick }: any) => (
  <div onClick={onClick} style={{
    background: '#1A2B3C', borderRadius: 14, padding: 20,
    border: `1px solid ${color}33`, cursor: onClick ? 'pointer' : 'default',
    transition: 'transform 0.15s',
  }}
    onMouseEnter={e => onClick && ((e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)')}
    onMouseLeave={e => ((e.currentTarget as HTMLDivElement).style.transform = 'none')}
  >
    <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
    <div style={{ fontSize: 32, fontWeight: 800, color }}>{value ?? '—'}</div>
    {value2 && <div style={{ fontSize: 14, fontWeight: 600, color: '#8FA8C0', marginTop: 2 }}>{value2}</div>}
    <div style={{ fontSize: 12, color: '#8FA8C0', marginTop: 4 }}>{label}</div>
    {sub && <div style={{ fontSize: 11, color: color + '99', marginTop: 2 }}>{sub}</div>}
  </div>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const canReviewCommissions = user?.role === 'admin';
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<any[]>([]);
  const [estimates, setEstimates] = useState<any[]>([]);
  const [commissions, setCommissions] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [cashPosition, setCashPosition] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [approving, setApproving] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoadError('');
    const fetchAll = async () => {
      try {
        const [jobsRes, estimatesRes, commissionsRes, leadsRes, receivablesRes] = await Promise.all([
          api.get('/jobs'),
          api.get('/estimates'),
          canReviewCommissions ? api.get('/commissions/pending') : Promise.resolve({ data: [] }),
          api.get('/leads'),
          api.get('/dashboard/receivables').catch(() => ({ data: null })),
        ]);
        if (!active) return;
        setCashPosition(receivablesRes.data);
        const scope = financialScope(jobsRes.data);
        setJobs(scope.jobs);
        setEstimates(scope.estimates(estimatesRes.data));
        setCommissions(scope.commissions(commissionsRes.data));
        setLeads(leadsRes.data);

      } catch (e) { if (active) setLoadError('Dashboard data could not be loaded. Refresh to retry.'); }
      finally { if (active) setLoading(false); }
    };
    fetchAll();
    return () => { active = false; };
  }, [canReviewCommissions]);

  const now = new Date();
  const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - now.getDay());
  const todayStr = now.toISOString().split('T')[0];
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const acRevenueMonth = jobs.filter(j => ['asphalt', 'concrete'].includes(j.job_type))
    .reduce((sum, j) => sum + Number(j.invoice_total_month || 0), 0);
  const roofJobs = jobs.filter(j => ['roofing', 'insurance'].includes(j.job_type) || !j.job_type);
  const roofingRevenueTotal = roofJobs.reduce((sum, j) => sum + Number(j.job_value || 0), 0);
  const roofingRevenueMonth = roofJobs.reduce((sum, j) => sum + Number(j.invoice_total_month || 0), 0);

  // Scheduled today & this week
  const scheduledToday = jobs.filter(j => j.scheduled_date === todayStr);
  const scheduledTomorrow = jobs.filter(j => j.scheduled_date === tomorrowStr);
  const scheduledWeek = jobs.filter(j => j.scheduled_date >= startOfWeek.toISOString().split('T')[0] && j.scheduled_date <= new Date(now.getTime() + 6 * 86400000).toISOString().split('T')[0]);

  // Estimates stats
  const estimatesDraft = estimates.filter(e => e.status === 'draft').length;
  const estimatesSent = estimates.filter(e => e.status === 'sent').length;
  const estimatesSignedWeek = estimates.filter(e => e.status === 'signed' && new Date(e.signed_at) >= startOfWeek).length;

  // Jobs by type
  const roofingJobs = jobs.filter(j => j.job_type === 'roofing' || !j.job_type);
  const acJobs = jobs.filter(j => ['asphalt', 'concrete'].includes(j.job_type));

  // Needs attention (roofing)
  const needsAttention = jobs.filter(j => ['Verify Claim Number/Checklist', 'Initial Insurance Estimate'].includes(j.pipeline_stage_name));

  // Deposit pending (AC jobs scheduled but no deposit)
  const depositPending = jobs.filter(j => j.scheduled_date && !j.deposit_received && ['asphalt', 'concrete'].includes(j.job_type));

  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const approveCommission = async (id: string, type: string, amount: number) => {
    if (!window.confirm(`Approve $${amount} commission?`)) return;
    setApproving(id);
    try {
      await api.post(`/commissions/${id}/approve`);
      setCommissions(prev => prev.filter(c => c.id !== id));
    } catch (e) { alert('Failed to approve'); }
    finally { setApproving(null); }
  };

  const fmtK = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;
  const fmt$ = (n: number) => `$${Math.round(n).toLocaleString()}`;

  return (
    <div style={{ padding: 28, maxWidth: 1400 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#fff', margin: 0 }}>
            {greeting}, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p style={{ color: '#8FA8C0', marginTop: 4, margin: 0 }}>
            {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {scheduledToday.length > 0 && (
            <div style={{ background: '#10B98122', border: '1px solid #10B98144', borderRadius: 12, padding: '8px 16px', color: '#10B981', fontWeight: 700, fontSize: 14 }}>
              🏗️ {scheduledToday.length} today
            </div>
          )}
          {scheduledTomorrow.length > 0 && (
            <div style={{ background: '#4A7EB522', border: '1px solid #4A7EB544', borderRadius: 12, padding: '8px 16px', color: '#4A7EB5', fontWeight: 700, fontSize: 14 }}>
              📅 {scheduledTomorrow.length} tomorrow
            </div>
          )}
        </div>
      </div>

      {/* 💰 Cash Position teaser — clicks through to /financials */}
      {cashPosition && (
        <div
          onClick={() => navigate('/financials')}
          style={{
            background: 'linear-gradient(135deg, #1A2B3C 0%, #243447 100%)',
            border: '1px solid #2D4259',
            borderRadius: 16,
            padding: 20,
            marginBottom: 20,
            cursor: 'pointer',
            transition: 'transform 0.15s, border-color 0.15s',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLDivElement).style.borderColor = '#4A7EB5';
            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLDivElement).style.borderColor = '#2D4259';
            (e.currentTarget as HTMLDivElement).style.transform = 'none';
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <div style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>💰 Cash Position</div>
              <div style={{ color: '#fff', fontSize: 32, fontWeight: 800, marginTop: 4 }}>
                {fmt$(cashPosition.total_outstanding)}
                <span style={{ color: '#8FA8C0', fontSize: 14, fontWeight: 600, marginLeft: 8 }}>outstanding across {cashPosition.open_invoice_count} invoices</span>
              </div>
            </div>
            <div style={{ color: '#4A7EB5', fontSize: 12, fontWeight: 600 }}>Full details →</div>
          </div>

          {/* Aging strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
            {[
              { label: '0–30d', value: cashPosition.aging.current, color: '#10B981' },
              { label: '31–60d', value: cashPosition.aging.thirty, color: '#F59E0B' },
              { label: '61–90d', value: cashPosition.aging.sixty, color: '#EF4444' },
              { label: '90+d', value: cashPosition.aging.ninety_plus, color: '#DC2626' },
            ].map(b => (
              <div key={b.label} style={{ background: '#0F1A26', border: `1px solid ${b.color}33`, borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ color: b.color, fontSize: 11, fontWeight: 700 }}>{b.label}</div>
                <div style={{ color: b.value > 0 ? '#fff' : '#4A6580', fontSize: 15, fontWeight: 700, marginTop: 2 }}>
                  {b.value > 0 ? fmtK(b.value) : '—'}
                </div>
              </div>
            ))}
          </div>

          {/* Sub-row: collected this month + top stale */}
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', color: '#8FA8C0', fontSize: 12, flexWrap: 'wrap' }}>
            <span>
              💵 <strong style={{ color: '#10B981' }}>{fmt$(cashPosition.collected.this_month)}</strong> collected this month
              {cashPosition.collected.delta_pct !== null && (
                <span style={{ marginLeft: 6, color: cashPosition.collected.delta_pct >= 0 ? '#10B981' : '#EF4444' }}>
                  ({cashPosition.collected.delta_pct >= 0 ? '↗' : '↘'} {Math.abs(cashPosition.collected.delta_pct).toFixed(0)}% vs last)
                </span>
              )}
            </span>
            {cashPosition.deductible_nudges.length > 0 && (
              <span>
                ⏰ <strong style={{ color: '#F59E0B' }}>{cashPosition.deductible_nudges.length}</strong> deductible link{cashPosition.deductible_nudges.length === 1 ? '' : 's'} pending &gt;7d
              </span>
            )}
            {cashPosition.coverage.underpaid_jobs.length > 0 && (
              <span>
                ⚠️ <strong style={{ color: '#EF4444' }}>{fmt$(cashPosition.coverage.total_exposure)}</strong> coverage gap ({cashPosition.coverage.underpaid_jobs.length} job{cashPosition.coverage.underpaid_jobs.length === 1 ? '' : 's'})
              </span>
            )}
            {cashPosition.top_stale[0] && (
              <span>
                🔴 Oldest: <strong style={{ color: '#EF4444' }}>{cashPosition.top_stale[0].homeowner_name}</strong> — {cashPosition.top_stale[0].days_stale}d ({fmt$(cashPosition.top_stale[0].amount_owed)})
              </span>
            )}
          </div>
        </div>
      )}

      {loadError && <p role="alert" style={{ color: '#F59E0B' }}>{loadError}</p>}
      {/* Top stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
        <StatCard icon="🏠" label="Roofing Revenue" value={`$${(roofingRevenueTotal / 1000).toFixed(1)}k`} color="#4A7EB5" onClick={() => navigate('/financials')} sub={`$${(roofingRevenueMonth / 1000).toFixed(1)}k this month`} />
        <StatCard icon="🛣️" label="AC Revenue This Month" value={`$${(acRevenueMonth / 1000).toFixed(1)}k`} color="#10B981" onClick={() => navigate('/financials')} sub="Invoice totals" />
        <StatCard icon="✍️" label="Signed This Week" value={estimatesSignedWeek} color="#4A7EB5" onClick={() => navigate('/estimates')} sub="New AC jobs" />
        {canReviewCommissions && <StatCard icon="💰" label="Pending Commissions" value={commissions.length} color="#F59E0B" onClick={() => navigate('/commissions')} sub={`$${commissions.reduce((s, c) => s + c.amount, 0).toFixed(0)} total`} />}
        <StatCard icon="📅" label="Scheduled This Week" value={scheduledWeek.length} color="#8B5CF6" onClick={() => navigate('/calendar')} sub={`${scheduledToday.length} today · ${scheduledTomorrow.length} tomorrow`} />
        <StatCard icon="⚠️" label="Needs Attention" value={needsAttention.length} color="#EF4444" onClick={() => navigate('/pipeline')} sub="Roofing" />
        <StatCard icon="💳" label="Deposit Pending" value={depositPending.length} color="#F59E0B" onClick={() => navigate('/pipeline')} sub="AC jobs" />
        <StatCard icon="📍" label="Total Leads" value={leads.length} color="#4A7EB5" onClick={() => navigate('/leads')} />
        <StatCard icon="🏠" label="Roofing Jobs" value={roofingJobs.length} color="#4A7EB5" onClick={() => navigate('/pipeline')} />
        <StatCard icon="🛣️" label="AC Jobs" value={acJobs.length} color="#F59E0B" onClick={() => navigate('/pipeline')} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

        {/* Estimates Pipeline */}
        <div style={{ background: '#1A2B3C', borderRadius: 16, border: '1px solid #2D4259', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #2D4259', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>📋 Estimates Pipeline</h2>
            <button onClick={() => navigate('/estimates')} style={{ background: 'none', border: '1px solid #2D4259', color: '#8FA8C0', padding: '4px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>View All</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, background: '#2D4259' }}>
            {[
              { label: 'Draft', value: estimatesDraft, color: '#64748B' },
              { label: 'Sent', value: estimatesSent, color: '#4A7EB5' },
              { label: 'Signed', value: estimates.filter(e => e.status === 'signed').length, color: '#10B981' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: '#1A2B3C', padding: 20, textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 12, color: '#8FA8C0', marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>
          {/* Recent estimates */}
          <div style={{ padding: '12px 20px' }}>
            {estimates.filter(e => e.status === 'signed').slice(0, 4).map(e => (
              <div key={e.id} onClick={() => navigate('/estimates')} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #2D425944', cursor: 'pointer' }}>
                <div>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{e.leads?.homeowner_name || e.homeowner_name || 'Signed Estimate'}</div>
                  <div style={{ color: '#8FA8C0', fontSize: 11 }}>{e.job_type} · {e.leads?.address?.split(',')[0] || '—'}</div>
                </div>
                <div style={{ color: '#10B981', fontWeight: 700, fontSize: 14 }}>${parseFloat(e.total_price || 0).toLocaleString()}</div>
              </div>
            ))}
            {estimates.filter(e => e.status === 'signed').length === 0 && (
              <div style={{ color: '#4A6580', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No signed estimates yet</div>
            )}
          </div>
        </div>

        {/* Pending Commissions */}
        {canReviewCommissions && <div style={{ background: '#1A2B3C', borderRadius: 16, border: '1px solid #2D4259', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #2D4259', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>💰 Pending Commissions</h2>
            <button onClick={() => navigate('/commissions')} style={{ background: 'none', border: '1px solid #2D4259', color: '#8FA8C0', padding: '4px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>View All</button>
          </div>
          <div style={{ padding: '8px 0' }}>
            {commissions.slice(0, 5).map(c => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #2D425944' }}>
                <div>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{c.users?.name}</div>
                  <div style={{ color: '#8FA8C0', fontSize: 11 }}>
                    {c.type === 'estimator' ? `🛣️ ${c.jobs?.job_type}` : '🏠 Roofing'} · {c.jobs?.leads?.homeowner_name || c.jobs?.homeowner_name || '—'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: '#F59E0B', fontWeight: 700, fontSize: 15 }}>${c.amount}</span>
                  <button
                    onClick={() => approveCommission(c.id, c.type, c.amount)}
                    disabled={approving === c.id || (c.type === 'estimator' && !['Job Complete','Pending Payment','Paid and Closed','Commission Paid','Closed'].includes(c.jobs?.pipeline_stage_name))}
                    title={c.type === 'estimator' && !['Job Complete','Pending Payment','Paid and Closed','Commission Paid','Closed'].includes(c.jobs?.pipeline_stage_name) ? 'Job must be complete first' : ''}
                    style={{ background: (c.type === 'estimator' && !['Job Complete','Pending Payment','Paid and Closed','Commission Paid','Closed'].includes(c.jobs?.pipeline_stage_name)) ? '#2D4259' : '#10B981', color: '#fff', padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', opacity: approving === c.id ? 0.6 : 1 }}
                  >
                    {approving === c.id ? '...' : (c.type === 'estimator' && !['Job Complete','Pending Payment','Paid and Closed','Commission Paid','Closed'].includes(c.jobs?.pipeline_stage_name)) ? '🔒' : '✓'}
                  </button>
                </div>
              </div>
            ))}
            {commissions.length === 0 && (
              <div style={{ color: '#4A6580', fontSize: 13, textAlign: 'center', padding: '30px 0' }}>🎉 No pending commissions</div>
            )}
          </div>
        </div>}
      </div>

      {/* Recent Jobs */}
      <div style={{ background: '#1A2B3C', borderRadius: 16, border: '1px solid #2D4259', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #2D4259', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Recent Jobs</h2>
          <button onClick={() => navigate('/pipeline')} style={{ background: '#4A7EB5', color: '#fff', padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none' }}>View Pipeline</button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2D4259' }}>
              {['Type', 'Homeowner', 'Address', 'Stage', 'Scheduled', 'Age'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jobs.slice(0, 10).map(job => {
              const days = Math.floor((Date.now() - new Date(job.created_at).getTime()) / 86400000);
              const stageColor = STAGE_COLORS[job.pipeline_stage_name] || '#4A7EB5';
              const typeIcon = job.job_type === 'asphalt' ? '🛣️' : job.job_type === 'concrete' ? '🪨' : '🏠';
              const displayName = job.leads?.homeowner_name || job.homeowner_name;
              const displayAddress = job.leads?.address || job.address;
              return (
                <tr key={job.id} onClick={() => navigate(`/pipeline/${job.id}`)}
                  style={{ borderBottom: '1px solid #2D4259', cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#243447')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '12px 16px', fontSize: 18 }}>{typeIcon}</td>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: '#fff', fontSize: 13 }}>{displayName}</td>
                  <td style={{ padding: '12px 16px', color: '#8FA8C0', fontSize: 12 }}>{displayAddress?.split(',').slice(0, 2).join(',')}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: stageColor + '22', color: stageColor, padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, border: `1px solid ${stageColor}44` }}>
                      {job.pipeline_stage_name?.replace('AC - ', '')}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: job.scheduled_date ? '#10B981' : '#4A6580', fontSize: 12, fontWeight: job.scheduled_date ? 600 : 400 }}>
                    {job.scheduled_date ? new Date(job.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: '#243447', color: '#8FA8C0', padding: '3px 8px', borderRadius: 8, fontSize: 11 }}>
                      {days === 0 ? 'Today' : `${days}d`}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {jobs.length === 0 && !loading && (
          <div style={{ padding: 40, textAlign: 'center', color: '#4A6580' }}>No jobs yet</div>
        )}
      </div>
    </div>
  );
}
